// @safetyqube/sq-ai-react — hooks.
//
// useSqAi() exposes the conversation to consumers. It surfaces BOTH the raw
// state (the testing UI renders state.messages directly — unchanged behaviour)
// and the additive convenience views from the client's selectors.

import { useContext } from "react";
import { SqAiContext } from "./SqAiProvider.js";
import {
  selectCurrentChart,
  selectStreamingText,
  selectIsThinking,
  selectThinkingPhase,
  selectAssumptions,
  selectResultMeta,
  selectError,
  selectLatestExport,
  selectCostDisplay,
} from "@safetyqube/sq-ai-client";

export function useSqAi() {
  const ctx = useContext(SqAiContext);
  if (!ctx) throw new Error("useSqAi() must be used within a <SqAiProvider>.");
  const { state, send, cancel, connected, connectionError, socket, sessionPosture } = ctx;
  return {
    // raw conversation state — the consumer renders state.messages itself
    state,
    messages: state.messages,
    status: state.status,
    send,
    cancel,
    // connection (for UI gating)
    connected,
    connectionError,
    // escape hatch for consumer-side listeners (e.g. the picker's ai:context_update)
    socket,
    // What the SERVER says this session may see: { accessScope: "super_admin" | "scoped" | "refused",
    // confidentialAccess }. Null until it has said. A host renders it; it is never derived
    // consumer-side, because the rule that decides it filters rows on the server.
    sessionPosture,
    // additive convenience views (derive from the same state; no new behaviour)
    isThinking: selectIsThinking(state),
    thinkingPhase: selectThinkingPhase(state),
    currentChart: selectCurrentChart(state),
    streamingText: selectStreamingText(state),
    assumptions: selectAssumptions(state),
    resultMeta: selectResultMeta(state),
    latestExport: selectLatestExport(state), // CSV export descriptor of the latest data turn (null otherwise)
    costDisplay: selectCostDisplay(state), // dev token/cost block; null unless the server opted in
    error: selectError(state),
    // In chart mode the agent writes clarifications as prose (promoted into the
    // turn caption), so there is no separate pending-clarify pause today. Exposed
    // for API completeness; answering a clarification is just sending the answer.
    pendingClarify: null,
    answerClarify: send,
  };
}

export function useSqAiChart() {
  const ctx = useContext(SqAiContext);
  if (!ctx) throw new Error("useSqAiChart() must be used within a <SqAiProvider>.");
  return selectCurrentChart(ctx.state);
}
